import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';

import { scopeKey } from '../api/endpoints.ts';
import type { Category, CollectionView, HomeBanner, RecentOrder, RecentOrderLine, StoreStatus } from '../api/types.ts';
import { formatRsd } from '../core/money.ts';
import { plateOf, useDarkScheme } from '../core/plate.ts';
import { useStore } from '../core/store.ts';
import { errorText, useLang, useStrings, type Strings } from '../i18n/index.ts';
import {
  requestCoverage,
  selectedAddress,
  selectedOutOfZone,
  storefrontScope,
  streetLine,
  useAddressBook,
} from '../state/address-book.ts';
import { syncCartWith } from '../state/cart.ts';
import { addWithChecks } from '../state/cart-sync.ts';
import { applyStockToHome, homeStore, homeTiles, loadHome, sectionIndexOf, visibleHomeItems } from '../state/home.ts';
import { lookupProduct, reorderAll } from '../state/reorder.ts';
import { openCart, openSignIn, pushRoute } from '../state/router.ts';
import { isSignedIn, useSession } from '../state/session.ts';
import { applyStoreStatus, isStoreDelayed, isStorePaused, localClock, notificationsStore, stockStore, storeInfoStore } from '../state/stores.ts';
import { notify } from '../state/toasts.ts';
import { Icon } from '../ui/icons.tsx';
import { AddButton, cx, EmptyState, ErrorBanner, pastel, pastelFor, RemoteImage, SectionHeader, Skeleton, Spinner } from '../ui/kit.tsx';
import { PayMarks } from '../ui/PayMarks.tsx';
import { ProductGrid, ProductLane } from '../ui/ProductTile.tsx';
import { Screen } from '../ui/Screen.tsx';
import { QuickFilters } from './CatalogScreen.tsx';
import '../styles/storefront.css';

/** Первые пиксели прокрутки шапка сжимается на месте, следующие — уезжает вверх и гаснет. */
const SHRINK_RANGE = 80;
const SCROLL_RANGE = 160;

/**
 * Главная: баннеры во всю ширину под шапкой с адресом, категории, быстрые фильтры, «Заказать
 * снова», подборки, скидки, популярное, новинки и покупки. Шапка своя, поверх ленты: при
 * прокрутке она сначала сжимается, затем уезжает вслед за баннером.
 */
export function HomeScreen() {
  const strings = useStrings();
  const lang = useLang();
  const session = useSession();
  const addressBook = useAddressBook();
  const home = useStore(homeStore);
  const liveStore = useStore(storeInfoStore);
  const stock = useStore(stockStore);
  const header = useRef<HTMLDivElement>(null);

  const scope = storefrontScope(addressBook);
  const signedIn = session.profile !== null;
  const reload = () => void loadHome(scope, lang, signedIn);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(reload, [scopeKey(scope), lang, signedIn]);

  useEffect(() => {
    if (stock.last) applyStockToHome(stock.last);
  }, [stock]);

  const feedStore = home.feed?.store;
  useEffect(() => {
    if (feedStore) applyStoreStatus(feedStore);
  }, [feedStore]);

  const visible = useMemo(() => visibleHomeItems(home), [home]);
  useEffect(() => syncCartWith(visible), [visible]);

  const tiles = useMemo(() => homeTiles(home.categories), [home.categories]);
  const address = selectedAddress(addressBook);
  const feed = home.feed;
  // Живой статус из ленты событий: смена режима видна без перезагрузки главной.
  const store = liveStore ?? feed?.store ?? null;

  const openBanner = (banner: HomeBanner) => {
    if (banner.collectionId) {
      pushRoute({ name: 'list', source: { kind: 'collection', id: banner.collectionId }, title: banner.title });
      return;
    }
    const category = home.categories.find((item) => item.id === banner.categoryId);
    if (category) pushRoute({ name: 'category', category });
  };

  return (
    <Screen
      className="home-screen"
      onScroll={(event) => {
        const node = header.current;
        if (!node) return;
        const offset = Math.min(Math.max(0, event.currentTarget.scrollTop), SCROLL_RANGE);
        const travel = Math.max(0, offset - SHRINK_RANGE);
        const fade = 1 - Math.min(1, travel / (SCROLL_RANGE - SHRINK_RANGE));
        node.style.setProperty('--header-scale', String(1 - 0.22 * Math.min(1, offset / SHRINK_RANGE)));
        node.style.setProperty('--header-travel', `${-travel}px`);
        node.style.opacity = String(fade);
        node.style.pointerEvents = fade > 0.3 ? '' : 'none';
      }}
    >
      <div className="home-header" ref={header}>
        <button type="button" className="address-plate" onClick={() => pushRoute({ name: 'addresses' })}>
          <span className="address-plate-title">
            <span>{address ? streetLine(address) : strings.chooseAddress}</span>
            <Icon name="chevronDown" size={11} weight={3.2} className="t-muted" />
          </span>
          <StoreStatusLine status={store} strings={strings} />
        </button>
        <BellButton strings={strings} />
      </div>

      <div className="home-feed">
        {home.error && !feed ? (
          <div className="page gutter home-top-inset">
            <ErrorBanner text={errorText(strings, home.error)} onRetry={reload} />
          </div>
        ) : !feed ? (
          <div className="page stack gutter home-top-inset" style={{ gap: 20 }}>
            <Skeleton height={170} />
            <div className="row" style={{ gap: 10 }}>
              <Skeleton width={150} height={210} />
              <Skeleton width={150} height={210} />
              <Skeleton width={150} height={210} />
            </div>
          </div>
        ) : (
          <>
            {feed.banners.length > 0 ? (
              <BannerCarousel
                banners={feed.banners}
                categoryImage={(banner) => home.categories.find((item) => item.id === banner.categoryId)?.imageUrl ?? null}
                onOpen={openBanner}
              />
            ) : (
              <div className="home-top-inset" />
            )}

            <div className="page stack" style={{ gap: 26 }}>
              {/* Категории сразу под баннером: главный путь в витрину. */}
              {tiles.length > 0 && <CategoryTiles categories={tiles} color={(category) => pastel(sectionIndexOf(home.categories, category))} />}

              {/* У адреса нет склада: полки выше — от склада по умолчанию, для заказа нужен другой адрес. */}
              {selectedOutOfZone(addressBook) && address && (
                <div className="gutter">
                  <OutOfZoneCard address={address.text} addressId={address.id} strings={strings} />
                </div>
              )}

              {store && (!store.isOpen || isStoreDelayed(store)) && (
                <div className="gutter">
                  <StoreClosedCard status={store} strings={strings} />
                </div>
              )}

              <QuickFilters strings={strings} order="home" />

              {home.recentOrders.length > 0 && (
                <section className="stack" style={{ gap: 12 }}>
                  <SectionHeader title={strings.orderAgain} actionTitle={strings.seeAll} onAction={() => pushRoute({ name: 'orders' })} />
                  <div className="lane snap">
                    {home.recentOrders.map((order) => (
                      <OrderAgainCard key={order.id} order={order} strings={strings} />
                    ))}
                  </div>
                </section>
              )}

              {feed.collections.map((collection) => (
                <CollectionBlock key={collection.id} collection={collection} strings={strings} />
              ))}

              {feed.deals.length > 0 && (
                <section className="plate deals-plate">
                  <div className="row plate-pad" style={{ justifyContent: 'space-between' }}>
                    <h2 className="t-display" style={{ fontSize: 26 }}>
                      {strings.deals}
                    </h2>
                    <span className="deals-mark">
                      <Icon name="percent" size={26} weight={2.6} />
                    </span>
                  </div>
                  <ProductLane items={feed.deals} pad={14} />
                  <div className="plate-pad">
                    <button
                      type="button"
                      className="plate-button is-accent pressable"
                      onClick={() => pushRoute({ name: 'list', source: { kind: 'deals' }, title: strings.deals })}
                    >
                      {strings.exploreDeals}
                    </button>
                  </div>
                </section>
              )}

              {/* У автоматических лент нет «Смотреть всё»: полный список новинок открывает чип выше,
                  а у популярного отдельного списка на сервере нет. */}
              {feed.mostOrdered.length > 0 && (
                <section className="stack" style={{ gap: 12 }}>
                  <SectionHeader title={strings.mostOrdered} />
                  <ProductLane items={feed.mostOrdered} />
                </section>
              )}

              {feed.newItems.length > 0 && (
                <section className="stack" style={{ gap: 12 }}>
                  <SectionHeader title={strings.newItems} />
                  <ProductLane items={feed.newItems} />
                </section>
              )}

              {home.fallback.length > 0 && (
                <section className="stack" style={{ gap: 12 }}>
                  <SectionHeader title={strings.homeProducts} />
                  <ProductGrid items={home.fallback} />
                </section>
              )}

              {visible.length === 0 && <EmptyState image="/empty/cart.webp" title={strings.homeSoldOut} hint={strings.homeSoldOutHint} />}

              {/* Покупки закрывают главную сеткой по три: «Заказать снова» выше уже показывает
                  те же товары по заказам, лента рядом повторяла бы их. */}
              {home.purchases.length > 0 && (
                <section className="stack" style={{ gap: 12 }}>
                  <SectionHeader
                    title={strings.youBought}
                    actionTitle={strings.seeAll}
                    onAction={() => pushRoute({ name: 'list', source: { kind: 'purchases' }, title: strings.youBought })}
                  />
                  <ProductGrid items={home.purchases} dense />
                </section>
              )}

            </div>
          </>
        )}
        {/* Знаки оплаты внизу главной при любом её содержимом: короткая лента прижимает их к вкладкам. */}
        <div className="page gutter home-paymarks">
          <PayMarks strip />
        </div>
      </div>
    </Screen>
  );
}

function BellButton({ strings }: { strings: Strings }) {
  const unread = useStore(notificationsStore).unread;
  return (
    <button
      type="button"
      className="home-bell"
      aria-label={strings.a11yNotifications(unread)}
      onClick={() => (isSignedIn() ? pushRoute({ name: 'notifications' }) : openSignIn())}
    >
      <Icon name="bell" size={20} />
      {unread > 0 && <span className="badge-count">{unread}</span>}
    </button>
  );
}

/** Строка статуса магазина под адресом: зелёная точка и «до 23:00» или «закрыто». */
function StoreStatusLine({ status, strings }: { status: StoreStatus | null; strings: Strings }) {
  if (!status) return null;
  const warn = !status.isOpen || isStoreDelayed(status);
  return (
    <span className="store-status-line">
      <i className={cx(warn && 'is-warn')} />
      {isStorePaused(status)
        ? strings.storePausedLine
        : status.isOpen
          ? strings.openUntil(localClock(status.closesAt, status.timezone), `${status.etaMinMinutes}–${status.etaMaxMinutes}`)
          : strings.closedOpensAt(localClock(status.opensAt, status.timezone))}
    </span>
  );
}

/**
 * Магазин закрыт, приём на паузе из-за наплыва или доставка идёт дольше: корзину собирать
 * можно, карточка объясняет, что с оформлением.
 */
function StoreClosedCard({ status, strings }: { status: StoreStatus; strings: Strings }) {
  const paused = isStorePaused(status);
  const delayed = isStoreDelayed(status);
  return (
    <div className="notice-card">
      {paused || delayed ? (
        <span className="notice-card-icon">
          <Icon name={paused ? 'pause' : 'clockAlert'} size={26} />
        </span>
      ) : (
        <img src="/empty/store-closed.webp" alt="" width={56} height={56} />
      )}
      <div className="grow">
        <h3 className="t-display">{paused ? strings.storePausedTitle : delayed ? strings.storeDelayedTitle : strings.storeClosedTitle}</h3>
        <p>
          {paused
            ? strings.storePausedHint
            : delayed
              ? strings.storeDelayedHint(status.demandExtraMinutes)
              : strings.storeClosedHint(localClock(status.opensAt, status.timezone))}
        </p>
      </div>
    </div>
  );
}

/** Выбранный адрес вне зон доставки: какой именно и как его сменить. */
function OutOfZoneCard({ address, addressId, strings }: { address: string; addressId: string; strings: Strings }) {
  const [requested, setRequested] = useState(false);
  const [sending, setSending] = useState(false);
  // «Сообщите, когда начнёте» просит сохранённый адрес: у точки гостя идентификатора на сервере нет.
  const canRequest = isSignedIn();

  return (
    <div className="notice-card">
      <span className="notice-card-icon is-danger">
        <Icon name="pinOff" size={26} />
      </span>
      <div className="grow">
        <h3 className="t-display">{strings.outOfZone}</h3>
        <p>{strings.outOfZoneHint(address)}</p>
        <div className="row" style={{ gap: 16, marginTop: 6, flexWrap: 'wrap' }}>
          {requested ? (
            <span className="row t-muted" style={{ gap: 4, fontSize: 15, fontWeight: 600 }}>
              <Icon name="check" size={15} weight={2.6} />
              {strings.coverageRequested}
            </span>
          ) : (
            canRequest && (
              <button
                type="button"
                className="text-button"
                disabled={sending}
                onClick={() => {
                  setSending(true);
                  requestCoverage(addressId)
                    .then(() => setRequested(true))
                    .catch(() => undefined)
                    .finally(() => setSending(false));
                }}
              >
                {strings.coverageRequest}
              </button>
            )
          )}
          <button type="button" className="text-button" onClick={() => pushRoute({ name: 'addresses' })}>
            {strings.changeAddress}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Лента плиток категорий: квадрат с картинкой и подпись. */
function CategoryTiles({ categories, color }: { categories: Category[]; color: (category: Category) => string }) {
  return (
    <div className="lane category-tiles">
      {categories.map((category) => (
        <button key={category.id} type="button" className="category-tile pressable" onClick={() => pushRoute({ name: 'category', category })}>
          <span className="category-tile-image" style={{ background: color(category) }}>
            <RemoteImage src={category.imageUrl} />
          </span>
          <span className="category-tile-name">{category.name}</span>
        </button>
      ))}
    </div>
  );
}

const BANNER_INTERVAL_MS = 5_000;

/** Карусель баннеров: страницы листаются сами, точки внизу. */
function BannerCarousel(props: { banners: HomeBanner[]; categoryImage: (banner: HomeBanner) => string | null; onOpen: (banner: HomeBanner) => void }) {
  const { banners } = props;
  const dark = useDarkScheme();
  const strip = useRef<HTMLDivElement>(null);
  const [page, setPage] = useState(0);
  const plates = banners.map((banner, index) => plateOf(banner.color, pastel(index), dark));

  // Одна плашка на всю ленту, она не листается вместе со страницами: её цвет смешивается между
  // соседними, а горизонтальный overscroll показывает ту же плашку.
  const blend = (position: number): string => {
    const clamped = Math.min(Math.max(position, 0), banners.length - 1);
    const lower = Math.floor(clamped);
    const upper = Math.min(lower + 1, banners.length - 1);
    const fraction = Math.round((clamped - lower) * 100);
    const from = plates[lower]?.fill ?? 'transparent';
    const to = plates[upper]?.fill ?? from;
    return fraction === 0 ? from : `color-mix(in srgb, ${to} ${fraction}%, ${from})`;
  };

  const onScroll = () => {
    const node = strip.current;
    if (!node || node.clientWidth === 0) return;
    const position = node.scrollLeft / pageWidth(node);
    node.style.setProperty('--banner-fill', blend(position));
    setPage(Math.min(Math.max(Math.round(position), 0), banners.length - 1));
  };

  // Отсчёт до следующей страницы начинается заново после любой смены страницы, в том числе
  // ручной: страница не убегает сразу после свайпа.
  useEffect(() => {
    if (banners.length < 2) return;
    const timer = setTimeout(() => {
      const node = strip.current;
      if (!node || node.offsetParent === null) return;
      const next = (page + 1) % banners.length;
      node.scrollTo({ left: next * pageWidth(node), behavior: 'smooth' });
    }, BANNER_INTERVAL_MS);
    return () => clearTimeout(timer);
  }, [page, banners.length]);

  const current = plates[page];
  return (
    <div className="banner-carousel">
      <div className="banner-strip" ref={strip} onScroll={onScroll} style={{ '--banner-fill': blend(0) } as CSSProperties}>
        {banners.map((banner, index) => {
          const plate = plates[index];
          const fallbackImage = props.categoryImage(banner);
          return (
            <button
              key={banner.id}
              type="button"
              className={cx('banner-card', plate?.onDark && 'on-dark')}
              style={{ '--plate-fill': plate?.fill } as CSSProperties}
              onClick={() => props.onOpen(banner)}
            >
              {banner.imageUrl ? (
                // Картинка баннера — квадратная горка товаров на прозрачном фоне: во всю высоту
                // плашки, прижата к низу и чуть заходит за правый край; текст остаётся слева.
                <span className="banner-image">
                  <RemoteImage src={banner.imageUrl} eager={index === 0} />
                </span>
              ) : fallbackImage ? (
                <span className="banner-fallback">
                  <RemoteImage src={fallbackImage} />
                </span>
              ) : (
                // Без фото справа крупная иконка: пустая пастель читается как незагруженная картинка.
                <span className="banner-fallback is-icon">
                  <Icon name={banner.collectionId ? 'sparkles' : 'basket'} size={44} />
                </span>
              )}
              <span className="banner-text">
                <span className="banner-title t-display">{banner.title}</span>
                {banner.subtitle && <span className="banner-subtitle">{banner.subtitle}</span>}
              </span>
            </button>
          );
        })}
      </div>
      {banners.length > 1 && (
        <div className={cx('banner-dots', current?.onDark && 'on-dark')}>
          {banners.map((banner, index) => (
            <i key={banner.id} className={cx(index === page && 'is-current')} />
          ))}
        </div>
      )}
    </div>
  );
}

/** Шаг страницы карусели: ширина карточки вместе с промежутком. */
function pageWidth(strip: HTMLElement): number {
  const first = strip.firstElementChild as HTMLElement | null;
  const second = first?.nextElementSibling as HTMLElement | null;
  if (first && second) return second.offsetLeft - first.offsetLeft;
  return strip.clientWidth;
}

/** Карточка прошлого заказа: товары на круглых подложках и «Добавить всё в корзину». */
function OrderAgainCard({ order, strings }: { order: RecentOrder; strings: Strings }) {
  const [adding, setAdding] = useState(false);
  const [added, setAdded] = useState(false);
  /** Маленькое фото, по которому тапнули: всплывающая карточка с названием, ценой и «в корзину». */
  const [peek, setPeek] = useState<RecentOrderLine | null>(null);

  // Первые три товара крупно с «плюсом», остальные мелко; дальше семи — счётчик.
  const large = order.items.slice(0, 3);
  const rest = order.items.slice(3);
  const stacked = rest.slice(0, 4);
  const overflow = rest.length - stacked.length;

  // Одна строка прошлого заказа в корзину с той же сверкой наличия, что у плитки.
  const add = (line: RecentOrderLine) =>
    void lookupProduct(line.productId).then((item) => (item ? addWithChecks(item) : notify(`${line.name}: ${strings.itemGone}`)));
  const open = (line: RecentOrderLine) => {
    setPeek(null);
    void lookupProduct(line.productId).then((item) => (item ? pushRoute({ name: 'product', item }) : notify(`${line.name}: ${strings.itemGone}`)));
  };

  return (
    <div className="reorder-card">
      <div className={cx('reorder-discs', rest.length > 0 && 'is-crowded')}>
        {large.map((line, index) => (
          <span key={line.productId} className="reorder-disc is-large" style={{ zIndex: large.length - index }}>
            <button type="button" className="reorder-photo pressable" onClick={() => open(line)} aria-label={line.name}>
              <RemoteImage src={line.photoUrl} />
            </button>
            <span className="reorder-plus">
              <AddButton enabled size={22} onClick={() => add(line)} />
            </span>
          </span>
        ))}
        {stacked.length > 0 && (
          <span className="reorder-small">
            {stacked.map((line, index) => (
              <button
                key={line.productId}
                type="button"
                className="reorder-disc reorder-photo pressable"
                style={{ zIndex: index }}
                aria-label={line.name}
                onClick={() => setPeek(peek?.productId === line.productId ? null : line)}
              >
                <RemoteImage src={line.photoUrl} />
              </button>
            ))}
            {overflow > 0 && <span className="reorder-disc reorder-more t-numeric">+{overflow}</span>}
          </span>
        )}
      </div>

      {peek && (
        <div className="product-peek">
          <button type="button" className="row grow pressable" style={{ gap: 12 }} onClick={() => open(peek)}>
            <span className="product-peek-photo">
              <RemoteImage src={peek.photoUrl} />
            </span>
            <span className="stack grow" style={{ gap: 3 }}>
              <span className="product-peek-name">{peek.name}</span>
              <b className="t-numeric" style={{ fontSize: 15, fontWeight: 700 }}>
                {formatRsd(peek.priceRsd)}
              </b>
            </span>
          </button>
          <AddButton
            enabled
            size={36}
            onClick={() => {
              add(peek);
              setPeek(null);
            }}
          />
        </div>
      )}

      <button
        type="button"
        className="reorder-all pressable"
        onClick={() => {
          if (adding) return;
          setAdding(true);
          // «Добавить всё»: состав прошлого заказа целиком в корзину, затем открывается корзина.
          void reorderAll(order).then((any) => {
            setAdding(false);
            setAdded(true);
            setTimeout(() => setAdded(false), 2_000);
            if (any) openCart();
          });
        }}
      >
        {adding ? <Spinner size={16} /> : <Icon name={added ? 'check' : 'cartPlus'} size={16} weight={2.8} />}
        <span>{added ? strings.addedToCart : strings.addAllToCart}</span>
      </button>
    </div>
  );
}

/** Подборка: цветная плашка с заголовком, лентой товаров и «Смотреть всё». */
function CollectionBlock({ collection, strings }: { collection: CollectionView; strings: Strings }) {
  const dark = useDarkScheme();
  const plate = plateOf(collection.color, pastelFor(collection.id), dark);
  const seeAll = () => pushRoute({ name: 'list', source: { kind: 'collection', id: collection.id }, title: collection.title });

  return (
    <section className={cx('plate', plate.onDark && 'on-dark')} style={{ background: plate.fill }}>
      {/* Только заголовок и подзаголовок: картинка подборки — сами товары в ленте. */}
      <button type="button" className="plate-pad plate-heading" onClick={seeAll}>
        <h2 className="t-display">{collection.title}</h2>
        {collection.subtitle && <p>{collection.subtitle}</p>}
      </button>
      <ProductLane items={collection.items} pad={14} />
      <div className="plate-pad">
        <button type="button" className="plate-button pressable" onClick={seeAll}>
          {strings.seeAll}
        </button>
      </div>
    </section>
  );
}
